import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type AdminKpiMetrics = {
  deals: {
    total: number;
    published: number;
    draft: number;
    sold30d: number;
  };
  users: {
    total: number;
    buyers: number;
    admins: number;
  };
  // seller_submissions.status IN ('new','in_progress') — "à traiter" pile.
  submissionsPending: number;
  // buyer_requests.status IN ('new','in_progress') — "à traiter" pile.
  requestsPending: number;
  // 0 when caller is admin (RLS blocks audit_log SELECT to super_admin only).
  audit_log_count: number;
};

export type AdminTopDeal = {
  id: string;
  reference_id: string | null;
  title: string;
  building: string | null;
  type: string;
  discount_pct: number | null;
  asking_price: number;
  requests_count_7d: number;
};

export type AdminRecentActivityRow = {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload: unknown;
  created_at: string;
  user_id: string | null;
};

// ADR-028 §D5 — `Sold deals` tile uses the same 30-day window as Spec §M9 L1289.
// `sold_at` is set by the FSM trigger when a deal transitions to status='sold'
// (003_deals.sql:121).
const SOLD_30D_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Aggregate counts for the admin dashboard. Runs the count-only queries in
 * parallel via Promise.all — each one is index-only (PostgREST `head: true`)
 * so payload stays minimal.
 *
 * `audit_log` query is fired unconditionally; RLS rejects non-super_admin
 * callers with an error which the dashboard surfaces as `audit_log_count = 0`
 * rather than throwing — admin still sees the tile but it shows 0.
 */
export async function getAdminKpiMetrics(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminKpiMetrics> {
  const sold30dIso = new Date(Date.now() - SOLD_30D_THRESHOLD_MS).toISOString();

  const [
    dealsTotal,
    dealsPublished,
    dealsDraft,
    dealsSold30d,
    usersAll,
    submissionsPending,
    requestsPending,
    auditCount,
  ] = await Promise.all([
    client.from('deals').select('*', { count: 'exact', head: true }).is('deleted_at', null),
    client
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'published')
      .is('deleted_at', null),
    client
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'draft')
      .is('deleted_at', null),
    client
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'sold')
      .gte('sold_at', sold30dIso)
      .is('deleted_at', null),
    client.from('users').select('role', { count: 'exact' }).is('deleted_at', null),
    client
      .from('seller_submissions')
      .select('*', { count: 'exact', head: true })
      .in('status', ['new', 'in_progress']),
    client
      .from('buyer_requests')
      .select('*', { count: 'exact', head: true })
      .in('status', ['new', 'in_progress']),
    client.from('audit_log').select('*', { count: 'exact', head: true }),
  ]);

  // Surface real backend errors (network / parse). audit_log RLS rejection is
  // expected for admin (non super_admin) callers — handled below as 0.
  if (dealsTotal.error) throw dealsTotal.error;
  if (dealsPublished.error) throw dealsPublished.error;
  if (dealsDraft.error) throw dealsDraft.error;
  if (dealsSold30d.error) throw dealsSold30d.error;
  if (usersAll.error) throw usersAll.error;
  if (submissionsPending.error) throw submissionsPending.error;
  if (requestsPending.error) throw requestsPending.error;

  type UserRoleRow = { role: 'buyer' | 'admin' | 'super_admin' };
  const usersRows = (usersAll.data ?? []) as UserRoleRow[];
  let buyers = 0;
  let admins = 0;
  for (const row of usersRows) {
    if (row.role === 'buyer') buyers += 1;
    else if (row.role === 'admin' || row.role === 'super_admin') admins += 1;
  }

  return {
    deals: {
      total: dealsTotal.count ?? 0,
      published: dealsPublished.count ?? 0,
      draft: dealsDraft.count ?? 0,
      sold30d: dealsSold30d.count ?? 0,
    },
    users: {
      total: usersAll.count ?? 0,
      buyers,
      admins,
    },
    submissionsPending: submissionsPending.count ?? 0,
    requestsPending: requestsPending.count ?? 0,
    audit_log_count: auditCount.error ? 0 : (auditCount.count ?? 0),
  };
}

/**
 * Top N published deals ranked by buyer_requests count over the last 7 days
 * (ADR-028 §D2 — Spec §M9 L1291 dérogation V1, views tracking dropped). Calls
 * the SECURITY DEFINER RPC `get_admin_top_deals` which encapsulates the LATERAL
 * COUNT + auth check (is_active_admin OR is_active_super_admin → 42501 for
 * other roles).
 */
export async function getAdminTopDeals(
  limit: number = 5,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminTopDeal[]> {
  const { data, error } = await client.rpc('get_admin_top_deals', { p_limit: limit });
  if (error) throw error;
  type RpcRow = {
    id: string;
    reference_id: string | null;
    title: string;
    building: string | null;
    type: string;
    discount_pct: number | null;
    asking_price: number;
    requests_count_7d: number;
  };
  return ((data ?? []) as RpcRow[]).map((row) => ({
    ...row,
    requests_count_7d: Number(row.requests_count_7d),
  }));
}

/**
 * Recent rows from `audit_log` for the dashboard activity feed.
 * RLS restricts SELECT to super_admin (audit_log_select policy in
 * 20260428040000_audit_log.sql). Non-super_admin callers get an empty
 * array silently — admins see "No activity" rather than an error.
 */
export async function getAdminRecentActivity(
  limit: number = 10,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminRecentActivityRow[]> {
  const { data, error } = await client
    .from('audit_log')
    .select('id, action, target_type, target_id, payload, created_at, user_id')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as AdminRecentActivityRow[];
}
